// EquipmentList (Domain/Customers; was CustomersViews.tsx, a flat view, HIERARCHY.md follow-up 8): every
// piece of equipment the company looks after, one row each, drawn from equipment.list's answer (pages/ux-full
// components/customers.js EquipmentCard, as a list). A row says what it is and where, how old, its warranty
// and the last visit, and what's due; its one action opens the customer's record on it. Status is its word:
// a service overdue is red, one coming up and a warranty about to end are waiting (dashed), a retired unit
// is muted. Five rows, then all of them on asking, then the next page. Reads only --eqlist-*; Link reads
// its own.
import { useState } from 'react';
import type { EquipmentListProps, EquipmentRow } from '../../customers-schemas.ts';
import { day, plural } from '../../format.ts';
import { Link } from '../Link/Link.tsx';
import './EquipmentList.css';

/** A service due within this many days is coming up; a warranty ending within this many is ending. */
export const DUE_SOON_DAYS = 30;
export const WARRANTY_SOON_DAYS = 60;
/** The rows shown before Show all. */
const FIRST = 5;

const daysFrom = (from: string, to: string) => Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000);

/** "11 years old", "Put in this year", or nothing when its install day isn't known. */
export function ageWords(e: Pick<EquipmentRow, 'ageYears'>): string | null {
  if (e.ageYears === null) return null;
  return e.ageYears === 0 ? 'Put in this year' : `${plural(e.ageYears, 'year')} old`;
}

export function warrantyWords(e: Pick<EquipmentRow, 'warranty'>): string {
  const w = e.warranty;
  if (w.state === 'covered') return w.lifetime ? 'Lifetime warranty' : `Covered to ${day(w.until)}`;
  if (w.state === 'ended') return w.until ? `Warranty ended ${day(w.until)}` : 'Warranty ended';
  return 'No warranty on file';
}

/** What needs someone about it, as a word and its tone; null when nothing does. */
export function dueOf(e: EquipmentRow, today: string): { word: string; text: string; bad: boolean } | null {
  if (e.retiredAt) return null;
  const s = e.serviceDue;
  if (s && s.on < today) return { word: 'Overdue', text: `${s.what} was due ${day(s.on)}`, bad: true };
  if (s && daysFrom(today, s.on) <= DUE_SOON_DAYS)
    return { word: 'Due soon', text: `${s.what} due ${day(s.on)}`, bad: false };
  const w = e.warranty;
  if (w.state === 'covered' && w.until && daysFrom(today, w.until) <= WARRANTY_SOON_DAYS)
    return { word: 'Warranty ending', text: `Warranty ends ${day(w.until)}`, bad: false };
  return null;
}

const where = (e: EquipmentRow) => `${e.customer.name}, ${e.location.name ?? e.location.line1}`;

export interface EquipmentListComponentProps {
  data: EquipmentListProps;
  /** Opens the customer's record on this piece (the screen's). */
  onOpen?: (e: EquipmentRow) => void;
  /** The next page, when there is one (equipment.list's next). */
  onMore?: () => void;
  /** A side list: what and whose, and what's due, on two lines. */
  brief?: boolean;
  /**
   * With nothing on file, the sentence it says between dashed rules. A page's region that says its own
   * empty state (ListPage) passes null, and the list draws nothing.
   */
  empty?: string | null;
}

/** Every customer's equipment, one row each; five, then all of the page, then the next page. */
export function EquipmentList({
  data,
  onOpen,
  onMore,
  brief = false,
  empty = 'No equipment on file yet. Technicians add it on the iPad when they see it.',
}: EquipmentListComponentProps) {
  const [all, setAll] = useState(false);
  const list = data.equipment;
  if (!list.length)
    return empty === null ? null : (
      <p className="fl-eqlist__empty" role="status">
        {empty}
      </p>
    );
  const rows = all ? list : list.slice(0, FIRST);
  return (
    <div className="fl-eqlist">
      <ul className="fl-eqlist__rows" aria-label="Equipment">
        {rows.map((e) => {
          const due = dueOf(e, data.today);
          const what = [e.make, e.model].filter(Boolean).join(' ');
          const facts = e.retiredAt
            ? [`Retired ${day(e.retiredAt.slice(0, 10))}`]
            : [
                ageWords(e),
                warrantyWords(e),
                e.lastVisit
                  ? `Last visit ${day(e.lastVisit.on)}, job #${e.lastVisit.jobNumber}`
                  : 'No visit there yet',
              ].filter((x): x is string => !!x);
          return (
            <li key={e.id} className="fl-eqlist__row" data-off={e.retiredAt ? '' : undefined}>
              <span className="fl-eqlist__main">
                {onOpen ? (
                  <button
                    type="button"
                    className="fl-eqlist__open"
                    aria-label={`${e.kind} at ${where(e)}: open their record`}
                    onClick={() => onOpen(e)}
                  >
                    {e.kind}
                  </button>
                ) : (
                  <span className="fl-eqlist__open">{e.kind}</span>
                )}
                <span className="fl-eqlist__sub">
                  {brief ? where(e) : [what, where(e), e.location.city].filter(Boolean).join(', ')}
                </span>
                {brief ? (
                  due && <span className="fl-eqlist__line">{due.text}</span>
                ) : (
                  <>
                    <span className="fl-eqlist__line">{facts.join('. ')}</span>
                    {due && <span className="fl-eqlist__sub">{due.text}</span>}
                  </>
                )}
              </span>
              {e.retiredAt ? (
                <span className="fl-eqlist__state" data-tone="muted">
                  Retired
                </span>
              ) : due ? (
                <span className="fl-eqlist__state" data-tone={due.bad ? 'bad' : 'wait'}>
                  {due.word}
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>
      {!all && list.length > FIRST ? (
        <p className="fl-eqlist__more">
          <Link onPress={() => setAll(true)}>Show all {list.length}</Link>
        </p>
      ) : onMore ? (
        <p className="fl-eqlist__more">
          <Link onPress={onMore}>Show more</Link>
        </p>
      ) : null}
    </div>
  );
}
